import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Users } from 'lucide-react'
import type { Team } from '@/api/types'
import { ToneBadge } from '@/components/ToneBadge'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'
import { useSession } from '@/hooks/useSession'
import { useWorldRefresh } from '@/hooks/useWorld'
import { DASH } from '@/ui/format'
import { ACTION, ACTION_DANGER, ACTION_PLAIN, CHIP } from './vm/chips'
import { pill } from './pills'
import { TeamCreateForm, TeamDeleteForm } from './TeamForms'
import { matchAny } from './model'
import type { World } from './model'

// 팀은 관리의 단위다(F-43·F-46): 소유자가 그 팀의 사람과 VM 을 관리한다. 그래서 이 화면은
// 누가 들어 있고 누가 관리하는가를 말한다. 태그는 그 팀이 무엇을 위한 것인가다.
// 팀은 쿼터를 갖지 않는다(F-88) — 쿼터는 사람의 직위 하나라 팀 쿼터 대비 사용량도 이 화면에 없다.
//
// **줄을 누르면 팀 상세(`TeamView.tsx`)로 간다** — 멤버·VM·태그를 보고 고치는 자리는 거기다. 이 목록은 고르고 거르는 곳이다.
// 줄의 VM 수는 서버가 싣는 `vm_count` 다 — 팀원이 지금 가진 VM 을 세려고 VM 전체를 읽지 않는다. 상세의 VM 표와 같은 집합이다.
// 표시 이름·사용 여부·`보기만` 자리는 이 화면에 없다 — 바꿨을 때 무엇이 되는지 한 낱말로 말할 수 없는 조작은 두지 않는다.
//
// **조작의 자리는 사용자 목록과 같다**(F-87). 팀 자체에 하는 `팀 생성`·`삭제` 는 제목 줄 오른쪽의 칩이고 삭제의 대상은
// 체크한 팀이다 — 체크박스는 관리하는 사람(소유자·최고 관리자)의 줄에만 선다. 칩은 늘 보이고 고른 팀이 없으면 잠긴다.
// 한 팀에 하는 `멤버 수정` 과 태그 고치기는 그 팀의 상세에 있다.
// 만들기와 삭제의 창은 사용자 목록의 `팀 관리` 창과 같은 것이다(`TeamForms`).

const tagsOf = (t: Team) => t.tags ?? []

// 대상은 객체가 아니라 id 로 든다 — 폴링이 목록을 갈아도 대화 상자가 옛 값을 보지 않는다.
// 삭제는 연 순간 체크돼 있던 팀에만 간다. 그 팀이 모두 사라지면 창도 닫힌다.
type Act = { kind: 'create' } | { kind: 'delete'; teamIds: string[] }

export default function TeamList({ world, query }: { world: World; query: string }) {
  const me = useSession()
  const nav = useNavigate()
  const [tag, setTag] = useState<string | null>(null)
  const [act, setAct] = useState<Act | null>(null)
  const [picked, setPicked] = useState<string[]>([])

  const all = world.teams
  const tags = [...new Set(all.flatMap(tagsOf))].sort((a, b) => a.localeCompare(b))
  // 고른 태그를 든 팀이 모두 사라지면(삭제·태그 수정) 거르개도 풀린다 — 그 태그의 알약이 없어
  // 풀 자리가 없는데 목록만 비어 있게 되지 않게.
  const active = tag !== null && tags.includes(tag) ? tag : null
  const rows = all
    .filter((t) => matchAny([t.display_name, t.name, ...tagsOf(t)], query))
    .filter((t) => active === null || tagsOf(t).includes(active))
  const manages = (t: Team) => me.role === 'superadmin' || t.my_role === 'owner'
  // 고른 줄이 검색·거르개·삭제로 사라지면 대상에서도 빠진다 — 보이지 않는 팀에 조작하지 않는다.
  const chosen = rows.filter((t) => manages(t) && picked.includes(t.id))
  // 체크박스가 서는 줄이 하나라도 있으면 서지 않는 줄은 그 폭을 비워 둔다 — 이름이 줄마다 다른 자리에서 시작하지 않게.
  const pickable = rows.some(manages)
  const pick = (id: string, on: boolean) => setPicked((v) => (on ? (v.includes(id) ? v : [...v, id]) : v.filter((x) => x !== id)))
  const pickTag = (x: string) => setTag(active === x ? null : x)
  const close = () => setAct(null)
  // 만든 팀과 고친 태그는 곧바로 목록에 선다 — 다음 폴링을 기다리면 쓰기가 실패한 것처럼 보인다.
  const refreshWorld = useWorldRefresh()
  const done = () => {
    setAct(null)
    refreshWorld()
  }
  const deleted = () => {
    setPicked([])
    done()
  }

  // 대상이 사라지면 대화 상자도 닫힌다 — 없는 것에 대고 물을 것이 없다.
  const doomed = act?.kind === 'delete' ? all.filter((t) => act.teamIds.includes(t.id)) : []
  const asking = act !== null && (act.kind === 'create' || doomed.length > 0)

  return (
    <>
      <SectionCrumb />
      <PageHead
        icon={<Users className="size-5" />}
        title="팀"
        afterTitle={
          tags.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={() => setTag(null)} aria-pressed={active === null} className={pill(active === null)}>
                전체
              </button>
              {tags.map((x) => (
                <button key={x} onClick={() => pickTag(x)} aria-pressed={active === x} className={pill(active === x)}>
                  {x}
                </button>
              ))}
            </div>
          ) : undefined
        }
        facts={chosen.length > 0 ? `${all.length}개 · ${chosen.length}개 선택` : `${all.length}개`}
        right={
          me.role !== 'user' ? (
            <div className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
              <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} onClick={() => setAct({ kind: 'create' })}>
                팀 생성
              </button>
              <button
                type="button"
                className={`${ACTION} ${ACTION_DANGER}`}
                disabled={chosen.length === 0}
                onClick={() => setAct({ kind: 'delete', teamIds: chosen.map((t) => t.id) })}
              >
                삭제
              </button>
            </div>
          ) : undefined
        }
      />

      <Section title="전체">
        {rows.length === 0 ? (
          <Empty>{query || active ? '검색 결과 없음' : '팀 없음'}</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {pickable ? <TableHead className="w-10 max-sm:hidden" /> : null}
                  <TableHead>팀</TableHead>
                  <TableHead className="max-sm:hidden">태그</TableHead>
                  <TableHead className="w-24 text-right">멤버</TableHead>
                  <TableHead className="w-24 text-right">VM</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} onClick={() => nav(`/teams/${t.id}`)} className="cursor-pointer hover:bg-surface-2">
                    {pickable ? (
                      <TableCell className="max-sm:hidden" onClick={(e) => e.stopPropagation()}>
                        {manages(t) ? (
                          <Checkbox
                            aria-label={`${t.display_name} 선택`}
                            checked={picked.includes(t.id)}
                            onCheckedChange={(v) => pick(t.id, v === true)}
                          />
                        ) : null}
                      </TableCell>
                    ) : null}
                    <TableCell className="font-medium max-sm:basis-full">
                      <span className="flex flex-wrap items-center gap-2">
                        <Users className="size-4 flex-none text-brand-hover" />
                        {t.display_name}
                        {t.my_role === 'owner' ? <ToneBadge tone="ok">관리</ToneBadge> : null}
                        {!t.enabled ? <ToneBadge tone="warn">비활성</ToneBadge> : null}
                      </span>
                    </TableCell>
                    {/* 줄의 태그는 표시가 아니라 거르개다(#307) — 칩을 누르면 상세로 가지 않고 목록을 좁힌다.
                        막는 것은 칩뿐이다: 칸 전체를 막으면 줄의 한가운데가 눌러도 아무 일 없는 자리가 된다. */}
                    <TableCell className="max-sm:hidden">
                      <span className="flex flex-wrap items-center gap-1.5">
                        {tagsOf(t).map((x) => (
                          <button
                            key={x}
                            onClick={(e) => {
                              e.stopPropagation()
                              pickTag(x)
                            }}
                            aria-pressed={active === x}
                            className={CHIP}
                          >
                            {x}
                          </button>
                        ))}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{t.members.length}명</TableCell>
                    <TableCell className="text-right tabular-nums">{t.vm_count === undefined ? DASH : `${t.vm_count}대`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Dialog open={asking} onOpenChange={(v) => (v ? undefined : close())}>
        <DialogContent aria-describedby={undefined}>
          {act?.kind === 'create' ? <TeamCreateForm onClose={close} onDone={done} /> : null}
          {act?.kind === 'delete' && doomed.length > 0 ? <TeamDeleteForm teams={doomed} onClose={close} onDone={deleted} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}
